import UnderlineLink from "@modules/common/components/underline-link"
import Image, { StaticImageData } from "next/image"
import clsx from "clsx"
import { FC, ReactNode } from "react"
import { ImageLoader } from "@lib/util/loader"

type ContactProps = {
    image: string | StaticImageData
    alt: string
    caption?: string
    title?: string
    subtitle?: string | ReactNode
    content?: string | ReactNode
    cta?: {
        text: string
        url: string
        position?:
            | "under title"
            | "under text left"
            | "under text right"
            | "under text 100%"
    }
    entranceAnimation?: boolean
}
const ContactElement: FC<ContactProps> = ({
    image,
    alt,
    caption,
    title,
    subtitle,
    content,
    cta,
    entranceAnimation = false,
}) => {
    return (
        <>
            <div
                className={clsx(
                    "w-full text-neutral-900",
                    "grid grid-cols-1 small:grid-cols-3 gap-0",
                    { "bg-white grain-background": entranceAnimation }
                )}
            >
                <div className="z-10 pt-10 small:pt-0 flex flex-col justify-center text-center md:text-left px-10 md:p-32 gap-10">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl 2xl:text-5xl font-display font-base subpixel-antialiased">
                        {title ?? ""}
                    </h1>
                    {cta && (
                        <UnderlineLink className="uppercase" href={cta.url}>
                            {cta.text}
                        </UnderlineLink>
                    )}
                    {content ?? ""}
                </div>
                <div className={"z-10 pt-[5vh] pr-2"}>
                    <div
                        className={
                            "relative aspect-[2/1] small:aspect-[9/10] w-full"
                        }
                    >
                        <span
                            className={
                                "absolute z-10 text-white ml-10 mt-10 font-semibold text-lg"
                            }
                        >
                            {caption ?? (
                                <>
                                    Design &amp;
                                    <br /> Duurzaam
                                </>
                            )}
                        </span>
                        <Image
                            loader={ImageLoader}
                            src={image}
                            layout="fill"
                            loading="eager"
                            objectFit="cover"
                            quality={90}
                            priority={true}
                            alt={alt ?? "Welcome to AKU Panel"}
                            className=""
                            draggable="false"
                            sizes="(max-width: 400px) 100vw,
                            (max-width: 800px) 100vw,
                            33vw"
                            placeholder={
                                typeof image === "string" ? "empty" : "blur"
                            }
                        />
                    </div>
                </div>
                <div
                    className={clsx(
                        "z-10 flex flex-col content-start justify-center pl-2 gap-8 pt-10 small:pt-[12vh]"
                    )}
                >
                    <div className="text-sm lg:text-base 2xl:text-lg w-full font-mono pl-[20%] pr-20 subpixel-antialiased">
                        {subtitle ?? ""}
                    </div>
                </div>
                {entranceAnimation && (
                    <div
                        className={
                            "absolute inset-x-0 bottom-0 top-[25vh] z-0 pointer-events-none"
                        }
                        style={{
                            background:
                                "linear-gradient(\n" +
                                "    to right,\n" +
                                "    transparent 0%,\n" +
                                "    transparent 33.33%,\n" +
                                "    #f6f6f6 33.33%,\n" +
                                "    #f6f6f6 100%\n" +
                                "  )",
                        }}
                    >
                        &nbsp;
                    </div>
                )}
            </div>
        </>
    )
}

export default ContactElement
